import React from "react";
import { Languages, Undo2, Settings2, X } from "lucide-react";
import { toast } from "sonner";

import { InfoBanner } from "@/main/react/component/InfoBanner";
import { Localizer } from "@/main/react/Types";
import { ClientFactory } from "@/main/common/ClientFactory";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { LANGUAGE_CONFIG, type LocalizationKey } from "@/main/util/LocalizationUtil";
import logger from "@/lib/logger";

const READING_LANGUAGE_ADDED_COOKIE = "seavien_reading_language_added";
const READING_LANGUAGES_SETTINGS_PATH = "/settings/reading_languages";

/** Display name for a language tag, falling back to the raw tag if it is not a known UI locale. */
function languageDisplayName(tag: string, localizer: Localizer): string {
    const entry = (LANGUAGE_CONFIG as Record<string, { textKey: LocalizationKey }>)[tag];
    return entry ? localizer(entry.textKey) : tag;
}

/** Read the one-shot "just auto-added" tag, then clear the cookie so the strip shows at most once. */
function readAndClearAddedTag(): string | null {
    if (typeof document === "undefined" || !document.cookie) {
        return null;
    }
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${READING_LANGUAGE_ADDED_COOKIE}=([^;]+)`));
    if (!match) {
        return null;
    }
    // Clear it immediately (same Path/SameSite as the server set it) so a reload doesn't re-show the strip.
    document.cookie = `${READING_LANGUAGE_ADDED_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
    const tag = decodeURIComponent(match[1]).trim().toLowerCase();
    return tag.length > 0 ? tag : null;
}

/**
 * The 1D "honest undo" strip (content-language Phase 2). When a member switches UI language via
 * {@code ?lang=} and we proactively add that language to their reading-set, the server drops a
 * short-lived {@code seavien_reading_language_added} cookie. This strip reads it once on the home feed,
 * shows a dismissible "Now showing X too — Undo · Manage" notice, then clears the cookie. Undo mutes
 * the just-added language (sticky); Manage deep-links to the reading-languages settings.
 *
 * <p>Design: {@code designs/00-inbox/2026-07-17-preferred-reading-languages-proactive-population-and-opt-out-ux.md}.
 */
export function ReadingLanguageAddedStrip({ localizer }: { localizer: Localizer }) {
    // Read + clear the cookie exactly once at mount so this is a genuine one-shot notice.
    const [tag, setTag] = React.useState<string | null>(() => readAndClearAddedTag());
    const [undoing, setUndoing] = React.useState(false);

    if (!tag) {
        return null;
    }

    const dismiss = () => setTag(null);

    const handleUndo = async () => {
        if (undoing) {
            return;
        }
        setUndoing(true);
        try {
            await ClientFactory.muteReadingLanguage(tag);
            toast.success(localizer("settings.reading-languages.success"));
            dismiss();
        } catch (err) {
            logger.error("[ReadingLanguageAddedStrip] Failed to mute reading language", err);
            toast.error(localizer("settings.reading-languages.error.save-failed"));
            setUndoing(false);
        }
    };

    const handleManage = () => {
        navigateToUrl(READING_LANGUAGES_SETTINGS_PATH, { source: "reading-language-added-strip-manage" });
    };

    return (
        <div className="mb-4" data-playwright-id="reading-language-added-strip">
            <InfoBanner
                icon={Languages}
                description={
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <span className="min-w-0">
                            {localizer("feed.reading-language-added.message", languageDisplayName(tag, localizer))}
                        </span>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                onClick={handleUndo}
                                disabled={undoing}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-medium text-text-main standard-hover disabled:opacity-60 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                data-playwright-id="reading-language-added-undo"
                            >
                                <Undo2 className="size-3.5" aria-hidden="true" />
                                {localizer("feed.reading-language-added.undo")}
                            </button>
                            <button
                                type="button"
                                onClick={handleManage}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-medium text-text-main standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                data-playwright-id="reading-language-added-manage"
                            >
                                <Settings2 className="size-3.5" aria-hidden="true" />
                                {localizer("feed.reading-language-added.manage")}
                            </button>
                            <button
                                type="button"
                                onClick={dismiss}
                                aria-label={localizer("onboarding.aria.dismiss")}
                                className="inline-flex items-center justify-center rounded-full border border-border/60 p-1.5 text-text-main standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                data-playwright-id="reading-language-added-dismiss"
                            >
                                <X className="size-3.5" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
}

export default ReadingLanguageAddedStrip;
